import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { CadsInfoBadgeBitConfig } from './cads-info-badge-bit.config';

/**
 * Info row bit: leading Material Symbols icon, label, and emphasized suffix. Optional semantic `color` applies the palette
 * family per row: pastel background, base border plus icon and label, **deep** token on the bold suffix only; otherwise neutral
 * chrome (muted surface, mid-gray icon, muted label, default strong text). Options via {@link CadsInfoBadgeBitConfig}
 * (`input()`; no i18n inside CADS v1).
 */
@Component({
    selector: 'cads-info-badge-bit',
    imports: [NgStyle],
    templateUrl: './cads-info-badge-bit.component.html',
    styleUrl: './cads-info-badge-bit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsInfoBadgeBitComponent {
  readonly config = input.required<CadsInfoBadgeBitConfig>();

  readonly hasAccent = computed(() => this.config().color != null);

  readonly accentCssVars = computed<Record<string, string>>(() => {
    const key = this.config().color;
    if (!key) {
      return {} as Record<string, string>;
    }
    return {
      '--cads-info-badge-bit-base': `var(--cads-color-${key})`,
      '--cads-info-badge-bit-deep': `var(--cads-color-${key}-deep)`,
      '--cads-info-badge-bit-pastel': `var(--cads-color-${key}-pastel)`,
    };
  });

  /** `aria-label`: {@link CadsInfoBadgeBitConfig.label} and {@link CadsInfoBadgeBitConfig.boldSuffix} trimmed and joined with one space (or a single part if the other is empty). */
  protected readonly _ariaLabel = computed(() => {
    const label = this.config().label.trim();
    const suffix = this.config().boldSuffix.trim();
    if (label === '') {
      return suffix;
    }
    if (suffix === '') {
      return label;
    }
    return `${label} ${suffix}`;
  });
}
